import './ObserversManagementModal.scss';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Rodal from '../../../../../components/Rodal/Rodal';
import { Col, FormGroup, ModalBody, ModalHeader, Row } from 'reactstrap';
import { MarketplaceBrokerUserInfo } from '../../../../../slices/marketplace/broker/models/broker';
import { EngagementObserver } from '../../../../../slices/marketplace/timeline/models/engagement-observer.model';
import createNotification from '../../../../../utils/createNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import UserDetailsModal from '../../../../Admin/Permissions/Users/Modal/UserDetailsModal';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import { addEngagementObserver, removeEngagementObserver } from '../../../../../slices/marketplace/timeline/timeline.slice';

interface ObserversManagementModalProps {
    engagementId: number;
    currentBrokerId?: number;
    initialObservers: EngagementObserver[];
    brokersList: MarketplaceBrokerUserInfo[];
    onClose: () => void;
}

const ObserversManagementModal = ({
    engagementId,
    currentBrokerId,
    initialObservers,
    brokersList,
    onClose,
}: ObserversManagementModalProps) => {
    const dispatch = useDispatch() as any;
    const axios = useSelector((state: any) => state.core.axios);
    const [observers, setObservers] = useState<EngagementObserver[]>(initialObservers);
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
    const [pendingRemoveObserver, setPendingRemoveObserver] = useState<EngagementObserver | undefined>(undefined);

    const addObserver = async (brokerId: number) => {
        try {
            await axios.post(
                `/api/Marketplace/Engagements/${engagementId}/observers/${brokerId}`
            );
            const broker = brokersList.find((b) => b.id === brokerId);
            setObservers((prev) => [
                ...prev,
                { id: 0, brokerId, brokerName: broker?.name ?? '' },
            ]);
            dispatch(addEngagementObserver({ engagementId, observer: { id: 0, brokerId, brokerName: broker?.name ?? '' } }));
        } catch {
            createNotification('Failed to add observer', 'error');
        }
    };

    const removeObserver = async (brokerId: number) => {
        try {
            await axios.delete(
                `/api/Marketplace/Engagements/${engagementId}/observers/${brokerId}`
            );
            setObservers((prev) => prev.filter((o) => o.brokerId !== brokerId));
            dispatch(removeEngagementObserver({ engagementId, brokerId }));
        } catch {
            createNotification('Failed to remove observer', 'error');
        }
    };

    const getObserverUserId = (brokerId: number): number | undefined =>
        brokersList.find((b) => b.id === brokerId)?.userId;

    const availableBrokers = brokersList.filter(
        (b) =>
            b.userId &&
            b.id !== currentBrokerId &&
            !observers.some((o) => o.brokerId === b.id)
    );

    return (
        <>
            <Rodal
                visible
                animation="fade"
                onClose={onClose}
                showMask
                width={500}
                closeOnEsc
                className="observers-management-popup"
                center>
                <ModalHeader>Manage Observers</ModalHeader>
                <ModalBody>
                    <div className="d-flex flex-column">
                        <FormGroup>
                            <Row>
                                <Col md={12}>
                                    <label className="copy-name-label">
                                        Current Observers
                                    </label>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    {observers.length === 0 ? (
                                        <p className="text-muted mt-1">
                                            No observers assigned.
                                        </p>
                                    ) : (
                                        <div className="observer-list">
                                            {observers.map((o) => {
                                                const userId = getObserverUserId(o.brokerId);
                                                return (
                                                    <div
                                                        key={o.brokerId}
                                                        className="observer-tile">
                                                        <span
                                                            className={`observer-name${userId ? ' observer-name--link' : ''}`}
                                                            onClick={
                                                                userId
                                                                    ? () => setSelectedUserId(userId)
                                                                    : undefined
                                                            }>
                                                            {o.brokerName}
                                                        </span>
                                                        <span
                                                            className="observer-delete"
                                                            onClick={() => setPendingRemoveObserver(o)}>
                                                            <FontAwesomeIcon icon={faXmark} />
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </FormGroup>
                        {availableBrokers.length > 0 && (
                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label className="copy-name-label">
                                            Add Observer
                                        </label>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <select
                                            className="form-control input-height"
                                            style={{ cursor: 'pointer' }}
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value)
                                                    addObserver(Number(e.target.value));
                                            }}>
                                            <option value="">
                                                Select advisor...
                                            </option>
                                            {availableBrokers.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                </Row>
                            </FormGroup>
                        )}
                    </div>
                </ModalBody>
            </Rodal>
            {selectedUserId && (
                <div className="user-details-popup">
                    <UserDetailsModal
                        onClose={() => setSelectedUserId(undefined)}
                        isVisisble={!!selectedUserId}
                        userID={selectedUserId}
                    />
                </div>
            )}
            <ConfirmationModal
                isVisible={!!pendingRemoveObserver}
                setIsVisible={() => setPendingRemoveObserver(undefined)}
                header="Remove Observer"
                confirmationText={`Are you sure you want to remove ${pendingRemoveObserver?.brokerName} from the engagement observers?`}
                onConfirm={() => removeObserver(pendingRemoveObserver!.brokerId)}
                onCancel={() => setPendingRemoveObserver(undefined)}
                nextButtonText="Remove"
                cancelButtonText="Cancel"
            />
        </>
    );
};

export default ObserversManagementModal;
